/**
 * iter87.378 (user-requested — "copy the NFL format") — NWSL Champions
 * Pick converted from a single instant-tap pick to the same 1-or-2
 * pick'em shape as `NflChampionPickModal.jsx`: 1 pick that wins pays
 * the full bonus (`tournament.scoring.nwsl_champion_points`, default
 * 15); 2 picks that include the winner pays the separate hedge value
 * (`tournament.scoring.nwsl_champion_hedge_points`, default 5 — NOT
 * half of 15). Multi-select + an explicit "Save" step (vs the old
 * instant single-tap submit) since there's a real choice to weigh.
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronRight, Crown, Check, Info, AlertTriangle } from "lucide-react";
import { BentoModal } from "@/components/onui/BentoMotion";
import { TeamSwatch } from "@/components/dashboard/TeamSwatch";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

export default function NwslChampionPickModal({ open, onClose, onSaved, tournamentId, participantId, existingPicks, bonusPoints = 15, hedgePoints = 5 }) {
    const [selected, setSelected] = useState(existingPicks || []);
    const [bracket, setBracket] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !tournamentId) return;
        setSelected(existingPicks || []);
        api.getNwslBracket(tournamentId).then(setBracket).catch(() => setBracket(null));
    }, [open, tournamentId, existingPicks]);

    if (!open) return null;
    const teams = (bracket?.teams || []).slice().sort((a, b) => a.seed - b.seed);
    const locked = !!bracket?.pre_season_locked;
    // iter87.444 — mirrors NflChampionPickModal.jsx's seeding-not-final
    // banner; NWSL's live-synced top-8 can still shift pre-lock.
    const fieldFinal = (bracket?.fixtures || []).length > 0;

    const toggle = (teamId) => {
        setSelected((prev) => {
            if (prev.includes(teamId)) return prev.filter((id) => id !== teamId);
            if (prev.length >= 2) {
                toast.error("You can pick at most 2 teams");
                return prev;
            }
            return [...prev, teamId];
        });
    };

    const save = async () => {
        if (selected.length < 1) {
            toast.error("Pick at least 1 team");
            return;
        }
        setSaving(true);
        try {
            await api.submitNwslChampionPick(tournamentId, participantId, selected);
            onSaved?.(selected);
            toast.success("NWSL Champion pick saved");
            onClose?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save your pick");
        } finally {
            setSaving(false);
        }
    };

    return (
        <BentoModal
            open={open}
            onClose={onClose}
            testId="nwsl-champion-pick-modal"
            backdropClassName="lg:items-center lg:justify-center lg:p-4"
            panelClassName="rounded-2xl border border-cyber/40 bg-obsidian max-w-lg w-full max-h-[90dvh] overflow-hidden flex flex-col"
        >
            <div className="flex items-center justify-between gap-2 px-4 sm:px-6 pt-4 sm:pt-6 pb-3 border-b border-white/[0.06] shrink-0">
                <div className="min-w-0 flex items-center gap-2 flex-1">
                    <div className="w-9 h-9 rounded-xl bg-cyber flex items-center justify-center shrink-0">
                        <Crown className="w-4 h-4 text-black" strokeWidth={2.5} />
                    </div>
                    <h3 className="font-display font-extrabold text-lg uppercase truncate">NWSL Champion</h3>
                </div>
                <button
                    onClick={onClose}
                    className="w-10 h-10 rounded-full bg-white/[0.06] hover:bg-white/[0.12] flex items-center justify-center transition-colors active:scale-95 shrink-0"
                    data-testid="close-nwsl-champion-pick-modal-btn"
                    aria-label="Close"
                >
                    <ChevronRight className="w-4 h-4 rotate-90" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">

            <div className="text-[11px] text-zinc-500 mb-4 flex gap-2 items-start text-left" data-testid="nwsl-champion-pick-explainer">
                <Info className="w-4 h-4 text-cyber shrink-0 mt-0.5" />
                <p className="leading-relaxed">
                    <span className="text-white font-bold uppercase tracking-tight">How it works:</span>{" "}
                    Pick 1 or 2 of the 8 playoff teams to win the Final. Locks the instant Round 1 (Quarter-finals) kicks off.
                    Pick <b className="text-zinc-300">1</b> and it wins → +{bonusPoints} pts. Pick <b className="text-zinc-300">2</b> and one wins → +{hedgePoints} pts (hedging costs you).
                </p>
            </div>

            {locked && (
                <p data-testid="nwsl-champion-locked-notice" className="text-heat text-[11px] uppercase tracking-widest font-bold mb-3">
                    Locked — Round 1 has started. Your pick(s) below are final.
                </p>
            )}

            {!locked && !fieldFinal && teams.length > 0 && (
                <div
                    data-testid="nwsl-champion-seeding-not-final-banner"
                    className="text-volt text-[11px] font-semibold flex items-start gap-1.5 bg-volt/10 border border-volt/40 rounded-lg px-3 py-2 mb-3"
                >
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>
                        Seeding isn't final yet — this top-8 field is projected only and can still shift before
                        the last day of the regular season. Come back to make your final pick once the season
                        ends and seeding is official.
                    </span>
                </div>
            )}

            <p className="mb-3 text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
                {locked ? "Your pick(s)" : "Choose up to 2"}
            </p>

            {teams.length > 0 ? (
                <div className="grid grid-cols-2 gap-2 mb-4" data-testid="nwsl-champion-team-grid">
                    {teams.map((t) => {
                        const isSelected = selected.includes(t.id);
                        return (
                            <motion.button
                                key={t.id}
                                data-testid={`nwsl-champion-option-${t.id}`}
                                disabled={saving || (locked && !isSelected)}
                                onClick={() => !locked && toggle(t.id)}
                                whileHover={!locked ? { y: -2, scale: 1.015 } : {}}
                                whileTap={!locked ? { scale: 0.975 } : {}}
                                transition={{ type: "spring", stiffness: 400, damping: 30 }}
                                className={`relative p-3 rounded-lg border text-left disabled:opacity-40 disabled:cursor-not-allowed ${
                                    isSelected
                                        ? "border-cyber bg-cyber/[0.08]"
                                        : "border-white/[0.08] bg-[#141414] hover:bg-[#181818] hover:border-cyber/40"
                                }`}
                            >
                                {isSelected && (
                                    <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-cyber flex items-center justify-center">
                                        <Check className="w-3 h-3 text-black" strokeWidth={3} />
                                    </div>
                                )}
                                <div className="flex items-center gap-2 min-w-0">
                                    <TeamSwatch name={t.name} sport="nwsl" size="w-7 h-7 text-[9px]" />
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-bold truncate">{t.name}</div>
                                        <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold mt-0.5">Seed #{t.seed}</div>
                                    </div>
                                </div>
                            </motion.button>
                        );
                    })}
                </div>
            ) : (
                <p className="text-xs text-zinc-500 rounded-lg border border-white/[0.08] bg-white/[0.02] p-3" data-testid="nwsl-champion-no-teams">
                    The playoff bracket isn't seeded yet — check back once the regular season wraps.
                </p>
            )}

            </div>

            {teams.length > 0 && (
                <div className="shrink-0 glass border-t border-white/10 px-4 sm:px-6 py-3" data-testid="nwsl-champion-pick-footer">
                    <div className="flex items-center gap-3">
                        <p className="font-display font-black text-sm uppercase tracking-tight flex-1 min-w-0 flex items-center flex-wrap gap-1.5">
                            <span>Up to 2</span>
                            <span className="text-zinc-600">·</span>
                            <span data-testid="nwsl-champion-selected-count" className="text-cyber">{selected.length}</span>
                            <span>{locked ? "picked" : "selected"}</span>
                            {selected.length === 2 && (
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <button
                                            type="button"
                                            data-testid="nwsl-champion-hedge-info-btn"
                                            aria-label="Why does hedging cost you?"
                                            className="w-5 h-5 rounded-full bg-heat/15 border border-heat/40 text-heat flex items-center justify-center shrink-0 normal-case active:scale-90 transition-transform"
                                        >
                                            <Info className="w-3 h-3" strokeWidth={2.5} />
                                        </button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                        data-testid="nwsl-champion-hedge-tooltip"
                                        side="top"
                                        align="start"
                                        className="z-[60] w-64 bg-obsidian border-heat/30 p-3"
                                    >
                                        <p className="text-[11px] text-zinc-300 leading-snug font-normal normal-case tracking-normal">
                                            <b className="text-heat">Why does it cost you?</b> 2 picks = 2 shots at the Champion bonus, so a win only pays +{hedgePoints} instead of +{bonusPoints}. Drop back to 1 team for the full payout.
                                        </p>
                                    </PopoverContent>
                                </Popover>
                            )}
                        </p>
                        {!locked && (
                            <button
                                type="button"
                                data-testid="nwsl-champion-save-btn"
                                onClick={save}
                                disabled={saving || selected.length < 1}
                                className="shrink-0 inline-flex items-center justify-center gap-1.5 rounded-full border-2 border-black bg-cyber text-black font-display font-extrabold uppercase tracking-wide text-xs px-4 min-h-[44px] transition-all duration-150 active:scale-95 disabled:opacity-40 disabled:pointer-events-none"
                            >
                                {saving ? "Saving…" : "Save Pick"}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </BentoModal>
    );
}
